import React, { FC } from 'react';
import { Animated, ImageSourcePropType, StyleProp, StyleSheet, TextStyle, View, ViewStyle } from 'react-native';

import { Theme } from '@constants';

import { styles } from './styles';

import { TWCText } from '../../primitives/TWCText/TWCText';
import { Progress } from '../TWCButton/types';
import { TWCButtonIcon } from '../TWCButtonIcon/TWCButtonIcon';

export const TWCButtonContent: FC<TWCButtonContentProps> = ({
  icon,
  iconFocused,
  title,
  disabled,
  progress,
  textStyle,
  focusStyle,
  styleFocused,
  focused,
}) => {
  const textFocusStyle = focusStyle ? focusStyle : styles.buttonTextFocused;
  const style = disabled
    ? [styles.buttonText, styles.buttonTextDisabled]
    : focused
    ? [styles.buttonText, textFocusStyle]
    : textStyle
    ? [styles.buttonText, textStyle]
    : styles.buttonText;

  const position = progress.animated || (progress.value && !progress.animated) ? 'absolute' : 'relative';

  const backgroundColors = {
    primary: focused ? Theme.white : Theme.withOpacity(Theme.white, 0.15),
    accent: focused ? Theme.withOpacity(Theme.white, 0.6) : Theme.transparent,
    content:
      progress.animated || (progress.value && !progress.animated)
        ? { backgroundColor: Theme.transparent }
        : focused
        ? StyleSheet.flatten([styles.buttonFocused, styleFocused])
        : {
            backgroundColor: Theme.transparent,
          },
  };

  const reverseValueStyle =
    typeof progress.value === 'number'
      ? 1 - progress.value
      : progress.value?.interpolate({ inputRange: [0, 1], outputRange: [1, 0] });

  return (
    <View importantForAccessibility={'no-hide-descendants'} style={[styles.buttonContainer, backgroundColors.content]}>
      {progress.animated ? ( // Countdown animated component
        <View style={styles.content}>
          <Animated.View
            style={{
              flex: progress.value,
              backgroundColor: backgroundColors.primary,
            }}
          />
          <Animated.View
            style={{
              backgroundColor: backgroundColors.accent,
              flex: reverseValueStyle,
            }}
          />
        </View>
      ) : null}
      {progress.value && !progress.animated ? ( // Static progress indicator component
        <View style={styles.content}>
          <Animated.View
            style={{
              flex: progress.value,
              backgroundColor: backgroundColors.primary,
            }}
          />
          <Animated.View
            style={{
              flex: reverseValueStyle,
              backgroundColor: backgroundColors.accent,
            }}
          />
        </View>
      ) : null}
      <View style={[styles.buttonContent, { position }]}>
        {icon ? (
          <TWCButtonIcon focused={focused} icon={icon} iconFocused={iconFocused} spacing={title ? Theme.px(18) : 0} />
        ) : null}
        {title ? (
          <TWCText noTestID style={style}>
            {title}
          </TWCText>
        ) : null}
      </View>
    </View>
  );
};

type TWCButtonContentProps = {
  icon?: ImageSourcePropType;
  iconFocused?: ImageSourcePropType;
  title?: string;
  disabled?: boolean;
  progress: Progress;
  textStyle?: StyleProp<TextStyle>;
  focusStyle?: StyleProp<TextStyle>;
  styleFocused?: StyleProp<ViewStyle>;
  focused?: boolean;
};
